// when overriding a default property, make sure to include all four properties
// or else mui will attempt to autogenerate them which does not work with css vars
const theme = {
  palette: {
    primary: {
      main: 'var(--color-white)',
      light: 'var(--color-white)',
      dark: 'var(--color-gray-950)', // somehow, used by mui picker calendar day
      contrastText: 'var(--color-gray-950)',
    },
    secondary: {
      main: 'var(--color-gray-950)',
      light: 'var(--color-gray-800)',
      dark: 'var(--color-gray-950)',
      contrastText: 'var(--color-white)',
    },
    tertiary: {
      main: 'var(--color-gray-200)',
      light: 'var(--color-gray-075)',
      dark: 'var(--color-gray-300)',
    },
    error: {
      main: 'var(--color-red-500)',
      light: 'var(--color-red-300)',
      dark: 'var(--color-red-700)',
      contrastText: 'var(--color-white)',
    },
    warning: {
      main: 'var(--color-red-500)',
      light: 'var(--color-red-300)',
      dark: 'var(--color-red-700)',
      contrastText: 'var(--color-white)',
    },
    success: {
      main: 'var(--color-green-500)',
      light: 'var(--color-green-300)',
      dark: 'var(--color-green-700)',
      contrastText: 'var(--color-white)',
    },
    info: {
      main: 'var(--color-gray-950)',
      light: 'var(--color-gray-800)',
      dark: 'var(--color-gray-950)',
      contrastText: 'var(--color-white)',
    },
    alternate: {
      main: 'var(--color-blue-200)',
    },
    text: {
      primary: 'var(--color-gray-950)',
      secondary: 'var(--color-gray-600)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        groupedHorizontal: {
          '&.MuiToggleButtonGroup-firstButton': {
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
          },
          '&.MuiToggleButtonGroup-lastButton': {
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
          },
        },
        groupedVertical: {
          '&.MuiToggleButtonGroup-firstButton': {
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          },
          '&.MuiToggleButtonGroup-lastButton': {
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-gray-950)',
          opacity: '0.6 !important',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: '8px 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          margin: 0,
          paddingBottom: 8,
          paddingTop: 8,
          paddingLeft: 16,
          paddingRight: 16,
          '& .MuiCheckbox-root': {
            padding: 0,
            paddingRight: 16,
          },
          '&.Mui-selected': {
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'var(--color-gray-075)',
            },
          },
          '&.MuiButtonBase-root': {
            display: 'flex',
            paddingBottom: 8,
            paddingTop: 8,
            paddingLeft: 16,
            paddingRight: 16,
          },
          '&:hover': {
            backgroundColor: 'var(--color-gray-075)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'var(--color-gray-950)',
          height: 20,
          width: 20,
          top: 'inherit',
        },
        outlined: {
          '&:valid, &:hover, &:focus, &:active': {
            borderRadius: 10,
          },
        },
        root: {
          height: 'auto',
        },
        select: {
          minHeight: 'auto',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          boxShadow: 'none',
          letterSpacing: 'normal',
          padding: '6px 16px',
          height: '32px',
          fontWeight: 400,
          fontSize: '0.8125rem',
          lineHeight: 1.23,
          '&.Mui-focusVisible': {
            outline: '2px solid var(--color-gray-950)',
            outlineOffset: 2,
          },
          variants: [],
        },
        contained: {
          boxShadow: 'none',
          fontWeight: 500,
          '&:hover, &:active, &.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: 'var(--color-gray-950)',
          '&:focus, &:hover': {
            backgroundColor: 'var(--color-gray-800)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'var(--color-gray-200)',
            color: 'var(--color-gray-300)',
          },
        },
        outlined: {
          borderColor: 'var(--color-gray-200)',
          color: 'var(--color-gray-950)',
          padding: '5px 15px',
          '&:focus, &:hover': {
            borderColor: 'var(--color-gray-950)',
          },
          '&.Mui-disabled': {
            color: 'var(--color-gray-200)',
            borderColor: 'var(--color-gray-200)',
          },
        },
        sizeSmall: {
          padding: '5px 12px',
          '& .MuiSvgIcon-root': {
            height: 20,
            width: 20,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: '2px solid var(--color-gray-950)',
            outlineOffset: 2,
          },
          '&.Mui-disabled': {
            color: 'var(--color-gray-200) !important',
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: 'var(--color-gray-950)',
          padding: '8px 16px',
          lineHeight: 1,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            fontWeight: 500,
          },
          '&:selected, &:focus': {
            backgroundColor: 'var(--color-gray-075)',
          },
        },
        button: {
          '&:hover, &:focus': {
            backgroundColor: 'var(--color-gray-075)',
          },
        },
        gutters: {
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'var(--color-gray-075)',
            '&:hover': {
              backgroundColor: 'var(--color-gray-075)',
            },
            '& .MuiTypography-root': {
              fontWeight: 500,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'var(--color-gray-950)',
          minWidth: 36,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        inset: {
          paddingLeft: 36,
        },
        primary: {
          lineHeight: 1,
          fontSize: '0.875rem',
        },
        secondary: {
          lineHeight: 1,
          fontSize: '0.875rem',
        },
        root: {
          marginBottom: 0,
          marginTop: 0,
        },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        child: {
          backgroundColor: 'white',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        hover: {
          cursor: 'pointer',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderBottom: '1px solid var(--color-gray-200)',
          lineHeight: 'normal',
          padding: 16,
          whiteSpace: 'nowrap',
        },
        head: {
          fontWeight: 500,
          color: 'var(--color-gray-950)',
          backgroundColor: 'var(--color-gray-075)',
          lineHeight: 'normal',
          padding: '12px 16px',
          '&:first-child': {
            borderTopLeftRadius: '10px',
          },
          '&:last-child': {
            borderTopRightRadius: '10px',
          },
        },
        stickyHeader: {
          backgroundColor: 'var(--color-gray-075)',
        },
        paddingCheckbox: {
          padding: 0,
          paddingLeft: 4,
          width: 48,
        },
        paddingNone: {
          padding: 0,
        },
        sizeSmall: {
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: 'normal',
          textTransform: 'none',
          minWidth: 80,
          whiteSpace: 'nowrap',
          '&.Mui-selected': {
            color: 'var(--color-gray-950)',
          },
          variants: [],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-white)',
          borderBottom: '1px solid var(--color-gray-200)',
          variants: [],
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          backgroundColor: 'var(--color-gray-950)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderRadius: 10,
        },
        marginDense: {
          height: 32,
        },
        root: {
          fontSize: '0.8125rem',
          position: 'relative',
          lineHeight: '1rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-gray-200)',
          },
          '& .MuiOutlinedInput-input': {
            height: 'auto',
          },
          '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
            {
              borderColor: 'var(--color-gray-950)',
              // Reset on touch devices, it doesn't add specificity
              '@media (hover: none)': {
                borderColor: 'var(--color-gray-200)',
              },
            },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-gray-950)',
            borderWidth: 1,
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-gray-200)',
            },
          },
          variants: [],
        },
        input: {
          padding: 12,
        },
        inputSizeSmall: {
          paddingTop: 8,
          paddingBottom: 8,
          paddingRight: 12,
          paddingLeft: 12,
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--color-gray-950)',
          '&.Mui-focused': {
            color: 'var(--color-gray-950)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--color-gray-950)',
          lineHeight: 1,
          '&.Mui-focused': {
            color: 'var(--color-gray-950)',
          },
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            fontSize: '1rem',
            transform: 'translate(0px, -24px)',
          },
        },
        sizeSmall: {
          color: 'var(--color-gray-600)',
          '&.Mui-focused': {
            color: 'var(--color-gray-600)',
          },
          '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            fontSize: '0.8125rem',
            transform: 'translate(16px, -21px)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          minWidth: 400,
          padding: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: '1px solid var(--color-gray-950)',
        },
        sizeSmall: {
          height: 20,
        },
        labelSmall: {
          fontSize: '0.6875rem',
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--color-gray-950)',
          borderRadius: 10,
          boxShadow: '0 1px 8px 0 var(--color-gray-200)',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&[class*="MuiOutlinedInput-root"]': {
            flexWrap: 'nowrap',
            padding: 12,
            '& .MuiAutocomplete-input, & .MuiAutocompleteinput:first-child': {
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
        option: {
          fontSize: '0.8125rem',
          paddingBottom: 8,
          paddingTop: 8,
          paddingLeft: 16,
          paddingRight: 16,
          '& .MuiCheckbox-root': {
            padding: 0,
            paddingRight: 16,
          },
        },
        loading: {
          fontSize: '0.8125rem',
        },
        noOptions: {
          fontSize: '0.8125rem',
        },
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiPickersToolbarButton: {
      styleOverrides: {
        toolbarBtn: {
          height: 'auto',
        },
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        toolbarTxt: {
          fontSize: '3.75rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        message: {
          color: 'var(--color-white)',
        },
        icon: {
          color: 'var(--color-white)',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'var(--color-gray-600)',
          '&.Mui-checked': {
            color: 'var(--color-gray-950)',
          },
          '&.MuiCheckbox-indeterminate': {
            color: 'var(--color-gray-600)',
          },
        },
        colorSecondary: {
          '&.Mui-disabled': {
            color: 'var(--color-gray-200)',
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--color-gray-075)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-gray-950)',
            color: 'var(--color-white)',
            '&:hover': {
              backgroundColor: 'var(--color-gray-800)',
            },
          },
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          '& .MuiButton-root': {
            backgroundColor: 'var(--color-gray-950)',
          },
        },
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          '&:hover': {
            backgroundColor: 'var(--color-gray-075)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-gray-075)',
            '&:hover': {
              backgroundColor: 'var(--color-gray-075)',
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: 'var(--color-gray-950)',
            '& + .MuiSwitch-track': {
              backgroundColor: 'var(--color-gray-500)',
              opacity: 1,
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: 'var(--color-gray-950)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        inputAdornedStart: {
          paddingLeft: '0 !important',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'var(--color-gray-200)',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-gray-200)',
        },
      },
    },
  },
};

export default theme;
